import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAppMode } from "@/contexts/AppModeContext";
import { getNavigationItems, getToolsItems } from "@/config/navigationItems";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarSeparator,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { QuickAddPanel } from "@/components/QuickAddPanel";
import { cn } from "@/lib/utils";
import { getProjectCodeFromPath, getSecondaryNavigation, visibleSecondaryNav, type NavigationGroup, type NavigationItem } from "@/config/navigation";
import { CountBadge } from "@/components/ui/count-badge";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useChannelUnread } from "@/hooks/useChannelUnread";
import { useInboxUnreadCount } from "@/hooks/useInboxMessages";
import { useOrgBasePath } from "@/hooks/useOrgNavigate";
import { isNavItemVisible, useNavVisibilityContext } from "@/config/navVisibility";

export function MainNavigation() {
  const { t } = useTranslation('common');
  const { state } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  // The hook owns the inbox Realtime subscription (see its comment in
  // useInboxMessages.ts) — it stays mounted here even though the primary nav
  // no longer renders an inbox badge from its count.
  useInboxUnreadCount();
  const { selectedOrganization } = useOrganization();
  // Project channels badge (docs/NAVIGATION.md "Badges"): keyed on the
  // project code in the URL, fetched only on a project detail page.
  const projectCode = getProjectCodeFromPath(currentPath);
  const { data: channelUnread } = useChannelUnread(
    selectedOrganization?.id,
    { contextType: 'project', contextCode: projectCode ?? undefined },
    { enabled: !!projectCode },
  );
  const orgBasePath = useOrgBasePath();
  const { mode } = useAppMode();
  const navVisibility = useNavVisibilityContext();

  // Get translated navigation items using useMemo
  const navigationItems = useMemo(() => getNavigationItems(t), [t]);
  const toolsItems = useMemo(() => getToolsItems(t), [t]);

  // Filter navigation items based on current mode and the shared visibility
  // gates (feature flag, org-role scope — docs/NAVIGATION.md "Visibility gates")
  const filterByModeAndFeature = useCallback(
    (item: typeof navigationItems[number]) =>
      item.modes.includes(mode) && isNavItemVisible(item, navVisibility),
    [mode, navVisibility]
  );
  const filteredNavigationItems = useMemo(
    () => navigationItems.filter(filterByModeAndFeature),
    [filterByModeAndFeature, navigationItems]
  );
  const filteredToolsItems = useMemo(
    () => toolsItems.filter(filterByModeAndFeature),
    [filterByModeAndFeature, toolsItems]
  );

  // Get secondary navigation items based on current path with i18n support
  const allSecondaryNavItems = useMemo(() => getSecondaryNavigation(currentPath, t), [currentPath, t]);

  // Filter out entries the caller may not see (super admin, org admin,
  // org-role scope, feature flag — one rule, `isNavItemVisible`), then any
  // heading left with no items under it.
  const secondaryNavItems = useMemo(
    () =>
      allSecondaryNavItems ? visibleSecondaryNav(allSecondaryNavItems, navVisibility) : null,
    [allSecondaryNavItems, navVisibility],
  );

  const hasSecondaryNav = secondaryNavItems && secondaryNavItems.length > 0;

  // Primary nav is narrow when there's secondary nav or sidebar is collapsed
  const isNarrow = hasSecondaryNav || state === "collapsed";
  const isSecondaryCollapsed = hasSecondaryNav && state === "collapsed";
  const [isNavHovered, setIsNavHovered] = useState(false);
  const [isSecondaryHovered, setIsSecondaryHovered] = useState(false);
  // Show text in secondary nav only when expanded or hovered
  const showSecondaryText = !isSecondaryCollapsed || isSecondaryHovered;


  const isActive = (href: string) => {
    const orgPathMatch = /^\/org\/[^/]+\/(.*)$/.exec(currentPath);
    const relativePath = orgPathMatch ? orgPathMatch[1] : '';

    if (href === 'dashboard') {
      return relativePath === '' || relativePath === 'dashboard' || relativePath.startsWith('dashboard/');
    }
    // Match on the top-level section (first path segment) so that e.g.
    // href="accounting/overview" stays active for all /accounting/* routes
    const section = href.split('/')[0];
    return relativePath === section || relativePath.startsWith(`${section}/`);
  };

  // Track expanded state for navigation groups (with localStorage persistence)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    if (secondaryNavItems) {
      secondaryNavItems.forEach((item) => {
        if (item.type === 'group') {
          try {
            const stored = localStorage.getItem(item.storageKey);
            initial[item.storageKey] = stored !== null ? stored === 'true' : true;
          } catch {
            initial[item.storageKey] = true;
          }
        }
      });
    }
    return initial;
  });

  const toggleGroup = useCallback((storageKey: string) => {
    setExpandedGroups((prev) => {
      const newValue = !prev[storageKey];
      try {
        localStorage.setItem(storageKey, String(newValue));
      } catch {
        // Ignore localStorage errors
      }
      return { ...prev, [storageKey]: newValue };
    });
  }, []);

  // Check if any item in a group is active
  const isGroupActive = useCallback((group: NavigationGroup) => {
    return group.items.some((item) => {
      const itemPath = item.href.replace(/^\//, '');
      const orgPathMatch = /^\/org\/[^/]+\/(.*)$/.exec(currentPath);
      const relativePath = orgPathMatch ? orgPathMatch[1] : '';
      return relativePath === itemPath || relativePath.startsWith(`${itemPath}/`);
    });
  }, [currentPath]);

  // Auto-expand groups when a child is active
  useEffect(() => {
    if (secondaryNavItems) {
      secondaryNavItems.forEach((item) => {
        if (item.type === 'group' && isGroupActive(item) && !expandedGroups[item.storageKey]) {
          setExpandedGroups((prev) => ({ ...prev, [item.storageKey]: true }));
        }
      });
    }
  }, [currentPath, secondaryNavItems, isGroupActive, expandedGroups]);

  return (
    <Sidebar
      hasSecondaryNav={hasSecondaryNav}
      collapsible="icon"
    >
      <div className="flex flex-1 h-full relative">
        {/* Primary Navigation Column — narrow w-12 with hover expansion when isNarrow */}
        <div
          className={cn("relative h-full", isNarrow ? "flex-none w-12" : "flex-1 overflow-y-auto")}
          onMouseEnter={() => isNarrow && setIsNavHovered(true)}
          onMouseLeave={() => setIsNavHovered(false)}
        >
          <SidebarContent
            className={cn(
              // pt-2 balances the group's own p-2 + content gap below the
              // "+ New" button (16px each side), centering it between the
              // header bar and the first divider
              "pt-2 pb-4",
              isNarrow && "absolute inset-0 transition-[width] duration-200 ease-in-out overflow-y-auto overflow-x-hidden",
              isNarrow && (isNavHovered ? "w-40 z-20 bg-sidebar shadow-lg" : "w-12")
            )}
          >
            {/* Fade gradient — starts just after the icons when collapsed */}
            {isNarrow && !isNavHovered && (
              <div
                className="absolute right-0 top-0 bottom-0 w-2 pointer-events-none z-10"
                style={{
                  background: 'linear-gradient(to right, transparent, hsl(var(--sidebar-background)))'
                }}
              />
            )}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <QuickAddPanel>
                  <SidebarMenuButton
                    tooltip={isNarrow && !isNavHovered ? { children: t("nav.quickAdd.trigger"), side: "right" } : undefined}
                    // Sidebar CTA tokens, not bg-primary: presets with a dark
                    // sidebar (purple) repoint these to an inverse pairing so
                    // the button stays visible on the rail. See docs/THEMING.md.
                    className="bg-sidebar-cta text-sidebar-cta-foreground hover:bg-sidebar-cta/90 hover:text-sidebar-cta-foreground active:bg-sidebar-cta/90 active:text-sidebar-cta-foreground data-[state=open]:bg-sidebar-cta data-[state=open]:hover:bg-sidebar-cta/90 data-[state=open]:text-sidebar-cta-foreground data-[state=open]:hover:text-sidebar-cta-foreground font-medium shadow-sm p-1.5 [&>svg]:size-5"
                  >
                    <Plus />
                    <span>{t("nav.quickAdd.trigger")}</span>
                  </SidebarMenuButton>
                </QuickAddPanel>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Groups are separated by slim dividers instead of text labels —
            labels added noise and truncated badly in the collapsed rail. */}
        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredToolsItems.map((item) => (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={isNarrow && !isNavHovered ? { children: item.name, side: "right" } : undefined}
                    // size-5 icons match the header org-switcher so the rail
                    // and header icons read at one size (overrides the
                    // variant's [&>svg]:size-4 via tailwind-merge)
                    className="p-1.5 [&>svg]:size-5"
                  >
                    <NavLink to={`${orgBasePath}/${item.href}`} end>
                      <item.icon />
                      <span>{item.name}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavigationItems.map((item) => (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={isNarrow && !isNavHovered ? { children: item.name, side: "right" } : undefined}
                    className="p-1.5 [&>svg]:size-5"
                  >
                    <NavLink to={`${orgBasePath}/${item.href}`} end>
                      <item.icon />
                      <span className="flex items-center gap-2">{item.name}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
        </div>

      {/* Secondary Navigation Column */}
      {hasSecondaryNav && (
        <div
          className={cn(
            "relative border-l border-sidebar-border h-full shrink-0 bg-sidebar-secondary",
            // Expanded: w-44 is the minimum; the column grows to fit the
            // widest (nowrap) label, capped so an extreme label can't crowd
            // out the page content.
            isSecondaryCollapsed ? "w-12" : "w-max min-w-44 max-w-72"
          )}
          onMouseEnter={() => isSecondaryCollapsed && setIsSecondaryHovered(true)}
          onMouseLeave={() => setIsSecondaryHovered(false)}
        >
          <div
            className={cn(
              "flex flex-col h-full",
              isSecondaryCollapsed && "absolute inset-0 transition-[width] duration-200 ease-in-out overflow-y-auto overflow-x-hidden",
              // Hover fly-out matches the expanded sizing (fit-content with
              // the same min/max). Width can't animate to max-content, so the
              // transition only eases the collapse back to w-12.
              isSecondaryCollapsed && (isSecondaryHovered ? "w-max min-w-44 max-w-72 z-20 bg-sidebar-secondary shadow-lg" : "w-12")
            )}
          >
            {/* Fade gradient when collapsed */}
            {isSecondaryCollapsed && !isSecondaryHovered && (
              <div
                className="absolute right-0 top-0 bottom-0 w-4 pointer-events-none z-10"
                style={{
                  background: 'linear-gradient(to right, transparent, hsl(var(--sidebar-secondary-background)))'
                }}
              />
            )}

            {/* Section Name Header */}
            <div className="px-3 py-3 border-b border-sidebar-border shrink-0 overflow-hidden">
              <div className="text-sm font-semibold text-sidebar-foreground whitespace-nowrap">
                {navigationItems.find(item => isActive(item.href))?.name || 'Navigation'}
              </div>
            </div>

            <div className="flex-1 overflow-auto p-2">
              <div className="flex flex-col gap-1">
                {secondaryNavItems.map((item) => {
                  if (item.type === 'heading') {
                    const isFirst = secondaryNavItems.indexOf(item) === 0;
                    return (
                      <div
                        key={item.name}
                        className={cn(
                          "px-2 h-8 flex items-center text-xs font-extralight text-sidebar-foreground/70 whitespace-nowrap",
                          !isFirst && "mt-3"
                        )}
                      >
                        {item.name}
                      </div>
                    );
                  }

                  if (item.type === 'group') {
                    const isExpanded = expandedGroups[item.storageKey] ?? true;
                    const groupActive = isGroupActive(item);

                    return (
                      <Collapsible
                        key={item.name}
                        open={showSecondaryText && isExpanded}
                        onOpenChange={() => toggleGroup(item.storageKey)}
                      >
                        <CollapsibleTrigger asChild>
                          <button
                            className={cn(
                              "flex w-full items-center gap-2 rounded-md p-2 h-8 text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground whitespace-nowrap",
                              groupActive ? "font-medium text-sidebar-accent-foreground" : "font-extralight"
                            )}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            {showSecondaryText && (
                              <>
                                <span className="flex-1 text-left">{item.name}</span>
                                {isExpanded ? (
                                  <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 opacity-50 shrink-0" />
                                )}
                              </>
                            )}
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-sidebar-border pl-2">
                            {item.items.map((subItem) => (
                              <NavLink
                                key={subItem.name}
                                to={`${orgBasePath}${subItem.href}`}
                                end={subItem.href.split('/').length <= 3}
                                className={({ isActive }) =>
                                  cn(
                                    "flex items-center gap-2 rounded-md p-2 h-8 text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground whitespace-nowrap",
                                    isActive ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'font-extralight'
                                  )
                                }
                              >
                                <subItem.icon className="h-4 w-4 shrink-0" />
                                {showSecondaryText && <span>{subItem.name}</span>}
                              </NavLink>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  }

                  return (
                    <NavLink
                      key={item.name}
                      to={`${orgBasePath}${item.href}`}
                      end={item.href.split('/').length <= 3}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 rounded-md p-2 h-8 text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground whitespace-nowrap",
                          isActive ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground' : 'font-extralight',
                          item.spaceBefore && "mt-3"
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {showSecondaryText && <span>{item.name}</span>}
                      {showSecondaryText && item.badgeKey === 'channel_unread' && (
                        <CountBadge
                          count={channelUnread?.unread_count}
                          // Not bg-primary: on the purple / blumenthal presets
                          // --primary equals the rail background (docs/THEMING.md).
                          className="ml-auto bg-sidebar-cta text-sidebar-cta-foreground"
                          srLabel={t('channels.unread', { count: channelUnread?.unread_count ?? 0 })}
                        />
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Sidebar>
  );
}
